import type { ShareableURLData, ReverseCalculationInput, SavedScenario } from '../../types/tax';

/**
 * URL sharing utilities for creating shareable links with calculation parameters
 */

const URL_VERSION = '1.0';

export function encodeCalculationURL(
  input: ReverseCalculationInput,
  frequency: 'monthly' | 'yearly' = 'monthly',
  scenarios?: SavedScenario[]
): string {
  const data: ShareableURLData = {
    netIncome: input.desiredNetIncome,
    ageCategory: input.ageCategory,
    frequency,
    advancedOptions: input.advancedOptions,
    scenarios,
    version: URL_VERSION
  };

  try {
    // Compress data by removing undefined/false values
    const compressedData = compressUrlData(data);
    const encodedData = btoa(JSON.stringify(compressedData));
    
    const url = new URL(window.location.origin);
    url.searchParams.set('calc', encodedData);
    
    return url.toString();
  } catch (error) {
    console.error('Failed to encode calculation URL:', error);
    // Fallback to basic parameters
    const url = new URL(window.location.origin);
    url.searchParams.set('net', input.desiredNetIncome.toString());
    url.searchParams.set('age', input.ageCategory);
    url.searchParams.set('freq', frequency);
    return url.toString();
  }
}

export function decodeCalculationURL(url: string): ShareableURLData | null {
  try {
    const urlObj = new URL(url);
    const calcParam = urlObj.searchParams.get('calc');
    
    if (calcParam) {
      // Decode full calculation data
      const decodedData = JSON.parse(atob(calcParam));
      return decompressUrlData(decodedData);
    }
    
    // Fallback: try legacy parameters
    const netIncome = urlObj.searchParams.get('net');
    const ageCategory = urlObj.searchParams.get('age');
    const frequency = urlObj.searchParams.get('freq') as 'monthly' | 'yearly';
    
    if (netIncome && ageCategory) {
      return {
        netIncome: parseFloat(netIncome),
        ageCategory: ageCategory as import('../../types/tax').AgeCategory,
        frequency: frequency || 'monthly',
        version: '0.9' // Legacy version
      };
    }
    
    return null;
  } catch (error) {
    console.error('Failed to decode calculation URL:', error);
    return null;
  }
}

export function createShareableLink(
  input: ReverseCalculationInput,
  frequency: 'monthly' | 'yearly',
  title?: string
): { url: string; title: string; text: string } {
  const url = encodeCalculationURL(input, frequency);
  const formatCurrency = (amount: number) => 
    new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount);

  const displayAmount = frequency === 'monthly' ? input.desiredNetIncome / 12 : input.desiredNetIncome;
  
  const defaultTitle = `SA Tax Calculator - ${formatCurrency(displayAmount)} ${frequency} net income`;
  const shareTitle = title || defaultTitle;
  
  const shareText = `Check out this SA tax calculation: ${formatCurrency(displayAmount)} ${frequency} net income requires specific gross salary. Calculate yours:`;

  return {
    url,
    title: shareTitle,
    text: shareText
  };
}

export function getCurrentCalculationURL(): string {
  return window.location.href;
}

export function updateURLWithCalculation(
  input: ReverseCalculationInput,
  frequency: 'monthly' | 'yearly',
  replaceHistory: boolean = true
): void {
  const url = encodeCalculationURL(input, frequency);
  const urlObj = new URL(url);
  
  if (replaceHistory) {
    window.history.replaceState({}, '', urlObj.pathname + urlObj.search);
  } else {
    window.history.pushState({}, '', urlObj.pathname + urlObj.search);
  }
}

// Helper functions for compression
function compressUrlData(data: ShareableURLData): Record<string, unknown> {
  const compressed: Record<string, unknown> = {
    n: data.netIncome,
    a: data.ageCategory,
    f: data.frequency,
    v: data.version
  };

  if (data.advancedOptions) {
    const adv: Record<string, unknown> = {};
    
    if (data.advancedOptions.medicalAid?.hasMedicalAid) {
      adv.m = {
        h: true,
        mm: data.advancedOptions.medicalAid.mainMember,
        d: data.advancedOptions.medicalAid.dependents
      };
    }
    
    if (data.advancedOptions.pension?.hasPension) {
      adv.p = {
        h: true,
        t: data.advancedOptions.pension.contributionType,
        a: data.advancedOptions.pension.contributionAmount
      };
    }
    
    if (data.advancedOptions.travelAllowance?.hasTravelAllowance) {
      adv.t = {
        h: true,
        v: data.advancedOptions.travelAllowance.vehicleValue,
        p: data.advancedOptions.travelAllowance.privateUsePercentage,
        a: data.advancedOptions.travelAllowance.travelAllowanceAmount
      };
    }
    
    if (Object.keys(adv).length > 0) {
      compressed.adv = adv;
    }
  }

  // Don't include scenarios in URL (too large), but note if they exist
  if (data.scenarios && data.scenarios.length > 0) {
    compressed.sc = data.scenarios.length;
  }

  return compressed;
}

function decompressUrlData(compressed: Record<string, unknown>): ShareableURLData {
  const data: ShareableURLData = {
    netIncome: compressed.n as number,
    ageCategory: compressed.a as import('../../types/tax').AgeCategory,
    frequency: (compressed.f as 'monthly' | 'yearly') || 'monthly',
    version: (compressed.v as string) || URL_VERSION
  };

  if (compressed.adv && typeof compressed.adv === 'object') {
    const adv = compressed.adv as Record<string, unknown>;
    data.advancedOptions = {};
    
    if (adv.m && typeof adv.m === 'object') {
      const medical = adv.m as Record<string, unknown>;
      data.advancedOptions.medicalAid = {
        hasMedicalAid: true,
        mainMember: medical.mm as boolean,
        dependents: medical.d as number
      };
    }
    
    if (adv.p && typeof adv.p === 'object') {
      const pension = adv.p as Record<string, unknown>;
      data.advancedOptions.pension = {
        hasPension: true,
        contributionType: pension.t as 'percentage' | 'fixed',
        contributionAmount: pension.a as number
      };
    }
    
    if (adv.t && typeof adv.t === 'object') {
      const travel = adv.t as Record<string, unknown>;
      data.advancedOptions.travelAllowance = {
        hasTravelAllowance: true,
        vehicleValue: travel.v as number,
        privateUsePercentage: travel.p as number,
        travelAllowanceAmount: travel.a as number
      };
    }
  }

  return data;
}